import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Stack,
  Button,
  Chip,
  Avatar,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Fade,
  Zoom,
  Tabs,
  Tab,
  Switch
} from '@mui/material';
import {
  Campaign as CampaignIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  Add as AddIcon,
  Image as ImageIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { GetAll, Put, apiURL } from '../../../utils/apiCalls';
import { toast } from 'react-toastify';
import { filter } from 'lodash';

const TABLE_HEAD = [
  { id: 'no', label: 'SNo.', align: 'left' },
  { id: 'name', label: 'Ad Name', align: 'left' },
  { id: 'position', label: 'Position', align: 'left' },
  { id: 'description', label: 'Description', align: 'left' },
  { id: 'is_active', label: 'Status', align: 'center' },
  { id: 'created_at', label: 'Created On', align: 'left' },
  { id: 'actions', label: 'Actions', align: 'center' },
];

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return filter(array, (_ad) => 
      _ad.name?.toLowerCase().indexOf(query.toLowerCase()) !== -1 ||
      _ad.description?.toLowerCase().indexOf(query.toLowerCase()) !== -1
    );
  }
  return stabilizedThis.map((el) => el[0]);
}

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`ads-tabpanel-${index}`}
      aria-labelledby={`ads-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `ads-tab-${index}`,
    'aria-controls': `ads-tabpanel-${index}`,
  };
}

const ProcurementAdsRequests = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [adPositions, setAdPositions] = useState([]);

  // Fetch ad positions for display
  useEffect(() => {
    const fetchAdPositions = async () => {
      try {
        const response = await GetAll(apiURL + '/api/v1/ads/positions/list');
        if (response && response.data && response.data.status) {
          let positions = response.data.data;
          if (!Array.isArray(positions)) {
            if (positions && typeof positions === 'object') {
              if (positions.models) {
                positions = positions.models.map(m => m.attributes || m);
              } else if (positions.toJSON) {
                positions = positions.toJSON();
              } else {
                positions = Object.values(positions);
              }
            }
            if (!Array.isArray(positions)) {
              positions = [];
            }
          }
          setAdPositions(positions);
        }
      } catch (error) {
        console.error('Error fetching ad positions:', error);
      }
    };
    fetchAdPositions();
  }, []);

  async function GetList() {
    setLoading(true);
    try {
      const response = await GetAll(apiURL + '/api/v1/ads/procurement/all');
      if (response && response.data && response.data.status) {
        const allAds = response.data.data || [];
        // Filter ads by procurement_id if needed (assuming user details are in sessionStorage)
        const userDetails = JSON.parse(sessionStorage.getItem('user_details') || '{}');
        const procurementId = userDetails.id;
        
        // If procurement_id exists in ad model, filter by it
        // Otherwise show all ads for now
        const filteredAds = procurementId 
          ? allAds.filter(ad => String(ad.procurement_id) === String(procurementId))
          : allAds;
        
        setList(Array.isArray(filteredAds) ? filteredAds : []);
      } else {
        toast.error(response?.data?.message || 'Failed to load ads');
        setList([]);
      }
    } catch (error) {
      console.error('Error fetching ads:', error);
      toast.error('An error occurred while fetching ads');
      setList([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    GetList();
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - list.length) : 0;

  const filteredAds = applySortFilter(list, getComparator('asc', 'id'), filterName);

  const isUserNotFound = filteredAds.length === 0;

  const handleViewClick = (id) => {
    navigate(`/dashboard/ads/create?id=${id}`);
  };

  const handleCreateRequest = () => {
    navigate('/dashboard/ads/create');
  };

  const handleToggleActive = async (e, id) => {
    const newStatus = e.target.checked;
    try {
      const response = await Put(apiURL + '/api/v1/ads/', id, {
        is_active: newStatus,
      });
      if (response && response.data && response.data.status) {
        toast.success(`Ad ${newStatus ? 'activated' : 'deactivated'} successfully`);
        GetList(); // Refresh the list
      } else {
        toast.error(response?.data?.message || 'Failed to update ad status');
        // Revert the change by refreshing
        GetList();
      }
    } catch (error) {
      console.error('Error updating ad status:', error);
      toast.error('An error occurred while updating ad status');
      // Revert the change by refreshing
      GetList();
    }
  };

  const getPositionName = (positionId) => {
    const position = adPositions.find(p => String(p.id) === String(positionId));
    return position?.name || `Position ${positionId}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Container sx={{ m: 0, width: '100%', maxWidth: '100%' }} maxWidth={false}>
      {/* Header Section */}
      <Fade in timeout={800}>
        <Box sx={{ mb: 4 }}>
          <Stack direction="row" alignItems="center" spacing={3} sx={{ mb: 3 }}>
            <Avatar sx={{
              bgcolor: '#ff6400',
              width: 48,
              height: 48,
              boxShadow: '0 4px 12px rgba(255, 100, 0, 0.3)'
            }}>
              <CampaignIcon sx={{ fontSize: 24, color: 'white' }} />
            </Avatar>
            <Box>
              <Typography variant="h5" component="h1" fontWeight="bold" color="text.primary">
                My Ads
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                Manage your advertisement requests
              </Typography>
            </Box>
          </Stack>
          <Divider sx={{ my: 2 }} />
        </Box>
      </Fade>

      {/* Tabs Section */}
      <Zoom in timeout={900}>
        <Card sx={{ mb: 3 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={activeTab} 
              onChange={handleTabChange}
              aria-label="ads tabs"
            >
              <Tab label="Ads List" {...a11yProps(0)} />
            </Tabs>
          </Box>
        </Card>
      </Zoom>

      {/* Tab Panel - Ads List */}
      <TabPanel value={activeTab} index={0}>
        {/* Action Bar */}
        <Zoom in timeout={1000}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2} justifyContent="space-between">
                <TextField
                  placeholder="Search by ad name or description..."
                  value={filterName}
                  onChange={handleFilterByName}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ maxWidth: 400 }}
                />
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleCreateRequest}
                  sx={{ minWidth: 180 }}
                >
                  Request Ads
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Zoom>

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={2.4}>
            <Zoom in timeout={1200}>
              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Avatar sx={{ bgcolor: '#ff6400', width: 40, height: 40 }}>
                      <CampaignIcon sx={{ fontSize: 20 }} />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight="bold" color="text.primary">
                        {list.length}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Ads
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Zoom>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Zoom in timeout={1400}>
              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Avatar sx={{ bgcolor: '#4caf50', width: 40, height: 40 }}>
                      <CheckCircleIcon sx={{ fontSize: 20 }} />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight="bold" color="text.primary">
                        {list.filter(item => item.is_active !== false).length}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Active Ads
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Zoom>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Zoom in timeout={1600}>
              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Avatar sx={{ bgcolor: '#f44336', width: 40, height: 40 }}>
                      <CancelIcon sx={{ fontSize: 20 }} />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight="bold" color="text.primary">
                        {list.filter(item => item.is_active === false).length}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Inactive Ads
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Zoom>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Zoom in timeout={1800}>
              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Avatar sx={{ bgcolor: '#2196f3', width: 40, height: 40 }}>
                      <ImageIcon sx={{ fontSize: 20 }} />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight="bold" color="text.primary">
                        {adPositions.length}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Ad Positions
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Zoom>
          </Grid>
        </Grid>

        {/* Ads Table */}
        <Zoom in timeout={2000}>
          <Card>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {TABLE_HEAD.map((headCell) => (
                      <TableCell
                        key={headCell.id}
                        align={headCell.align}
                      >
                        {headCell.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          Loading...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : filteredAds.length > 0 ? (
                    filteredAds
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((row, index) => {
                        const isActive = row.is_active !== undefined ? row.is_active : true;
                        return (
                          <TableRow key={row.id} hover>
                            <TableCell align="left">
                              <Typography variant="body2" color="text.secondary">
                                {page * rowsPerPage + index + 1}
                              </Typography>
                            </TableCell>
                            <TableCell align="left">
                              <Typography variant="body1" fontWeight="bold">
                                {row.name || 'Untitled Ad'}
                              </Typography>
                            </TableCell>
                            <TableCell align="left">
                              <Chip
                                label={getPositionName(row.position_id)}
                                color="primary"
                                size="small"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell align="left">
                              <Typography variant="body2" color="text.secondary" sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: 300
                              }}>
                                {row.description || 'No description'}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Stack direction="row" alignItems="center" spacing={1} justifyContent="center">
                                <Chip
                                  label={isActive ? 'Active' : 'Inactive'}
                                  size="small"
                                  sx={{
                                    background: isActive ? '#4caf50' : '#f44336',
                                    color: 'white',
                                    fontWeight: 500
                                  }}
                                />
                                <Switch
                                  checked={isActive}
                                  onChange={(e) => handleToggleActive(e, row.id)}
                                  sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                      color: '#4caf50',
                                    },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                      backgroundColor: '#4caf50',
                                    },
                                  }}
                                />
                              </Stack>
                            </TableCell>
                            <TableCell align="left">
                              <Typography variant="body2" color="text.secondary">
                                {formatDate(row.created_at)}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Stack direction="row" spacing={1} justifyContent="center">
                                <Tooltip title="View Details">
                                  <IconButton
                                    color="primary"
                                    onClick={() => handleViewClick(row.id)}
                                    size="small"
                                  >
                                    <VisibilityIcon />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })
                  ) : null}
                  {emptyRows > 0 && filteredAds.length > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={7} />
                    </TableRow>
                  )}
                </TableBody>

                {!loading && isUserNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={7} sx={{ py: 3 }}>
                        <Typography gutterBottom align="center" variant="subtitle1">
                          No ads found
                        </Typography>
                        <Button
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={handleCreateRequest}
                          sx={{ mt: 2 }}
                        >
                          Create Your First Ad Request
                        </Button>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
            {!loading && filteredAds.length > 0 && (
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={filteredAds.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            )}
          </Card>
        </Zoom>
      </TabPanel>
    </Container>
  );
};

export default ProcurementAdsRequests;

